<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { onBeforeRouteUpdate, useRoute, useRouter } from 'vue-router'
import UIKit from '@pocketprep/ui-kit'
import StudentStatsHead from '@/components/Stats/StudentStats/StudentStatsHead.vue'
import SectionHead from '@/components/Stats/SectionHead.vue'
import SubjectInsights from '@/components/Stats/StudentStats/SubjectInsights.vue'
import QuizScores from '@/components/Stats/StudentStats/QuizScores.vue'
import StudentQuestionCard from '@/components/StudentQuestionCard.vue'
import Activities from '@/components/Stats/StudentStats/Activities.vue'
import Streaks from '@/components/Stats/StudentStats/Streaks.vue'
import ProgressBar from '@/components/Stats/ProgressBar.vue'
import PendingStudent from '@/components/PendingStudent.vue'
import ContentVersionMessaging from '@/components/ContentVersionMessaging.vue'
import { monthNames } from '@/utils'
import { examModule } from '@/store/exam/module'
import { statsModule } from '@/store/stats/module'
import { groupModule } from '@/store/group/module'
import { studentModule } from '@/store/student/module'
import type { Teach } from '@pocketprep/types'
import { analyticsModule } from '@/store/analytics/module'
import { licenseModule } from '@/store/license/module'

type TStudentQuestionCardInfo = {
    serial: string
    subject: string
    prompt: string
    unread: boolean
    answeredCorrectly: boolean
    unanswered: boolean
}

const {
    Link: PocketLink,
    EmptyState,
} = UIKit

const route = useRoute()
const router = useRouter()

const numOfFlaggedQuestions = ref(0)
const page = ref('stats')
const componentKey = ref(0) // For re-rendering the progress bar when user swap groups
const totalProgress = ref(0)
const progressComplete = ref(false)

const studentStats = computed(() => statsModule.getters.getCurrentStudentStats())

const isLoading = computed(() => studentStats.value.isLoading)

const currentStudentStats = computed(() => statsModule.getters.getCurrentStudentStats())

const currentStudentCurrentExam = computed((): Teach.Class.OrgExamJSON | null => {
    return examModule.getters.getCurrentStudentCurrentExam()
})

const currentOrgLicenses = computed(() => licenseModule.getters.getCurrentOrgLicenses())

const currentOrgPendingLicenses = computed(() => {
    return currentOrgLicenses.value.filter(l => l.status === 'pending')
})

const studentFlaggedQuestions = computed(() => {
    const flaggedQs: TStudentQuestionCardInfo[] = []

    // checking to see if a student has an unanswered flagged question
    if (studentStats.value.flaggedQuestions.length) {
        studentStats.value.flaggedQuestions.forEach(q => {
            // if they have answeredQuestions we can check the question
            // against their answered questions to see if it is answered and flagged
            if (!q.isMockQuestion) {
                if (studentStats.value.answeredQuestions.length) {
                    const answeredFlaggedQ = studentStats.value.answeredQuestions.find(
                        question => question.question.serial === q.serial
                    )

                    if (answeredFlaggedQ) {
                        const addUnansweredField = { ...q, unanswered: false }
                        flaggedQs.push(addUnansweredField)
                    } else {
                        const addUnansweredField = { ...q, unanswered: true }
                        flaggedQs.push(addUnansweredField)
                    }

                } else {
                    // else as we loop through the flagged questions
                    // we can add the unanswered flagged questions to the array
                    // with the modified field (unanswered: true)
                    const addUnansweredField = { ...q, unanswered: true }
                    flaggedQs.push(addUnansweredField)
                }
            }
        })
        return flaggedQs
    }
    return []
})

const unreadCount = computed(() => {
    if (currentStudentStats.value?.flaggedQuestions?.length) {
        return currentStudentStats.value.flaggedQuestions.filter(qd => qd.unread && !qd.isMockQuestion).length
    } else {
        return 0
    }
})

const examName = computed(() => {
    const exam = currentStudentCurrentExam.value
    if (exam) {
        return exam.displayName || ('nativeAppName' in exam.examMetadata && exam.examMetadata.nativeAppName)
    } else {
        return ''
    }
})

const isPendingStudent = computed(() => {
    const routeStudentId = route.params.studentId
    const isCurrentStudentPending = !!currentOrgPendingLicenses.value
        .find(opl => opl.orgStudent?.objectId === routeStudentId)
    return route.query.isPending === 'true' || isCurrentStudentPending
})

const checkLoading = () => {
    if (!isLoading.value) {
        // No need to poll if things are already loaded
        progressComplete.value = true
    } else {
        const timer = window.setInterval(() => {
            if (isLoading.value) {
                totalProgress.value++
            } else {
                totalProgress.value = 100
                clearInterval(timer)
                window.setTimeout(() => {
                    progressComplete.value = true
                }, 500) // Should match the css transition time
            }
        }, 100)
    }
}

const getDaysUntil = (dateString: string) => {
    const scheduledExamDate = new Date(dateString).getTime()
    const currentDate = new Date(new Intl.DateTimeFormat().format()).getTime()

    return scheduledExamDate
        ? Math.floor((scheduledExamDate - currentDate) / 1000 / 60 / 60 / 24)
        : -1
}

const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return `${monthNames[date.getMonth()]} ${date.getDate()}`
}

const viewAll = () => {
    router.push({ name: 'student-content', query: { tab: 'flagged' } })

    analyticsModule.actions.amplitudeTrack('Button Clicked', {
        buttonContext: 'Student Stats Flagged Questions View All Button',
    })
}

const openQuestionReview = (questionSerial: string, type: string) => {
    if (questionSerial) {
        router.push({
            name: route.name as string,
            params: { ...route.params },
            query: {
                questionSerial,
                type: type,
            },
        })
        analyticsModule.actions.amplitudeTrack('List Item Clicked', {
            listItemContext: 'Student Stats Flagged Question',
            listItemValue: questionSerial,
        })
    }
}

onBeforeRouteUpdate((to, from, next) => {
    // Re-initialize all our stuff to maintain progress state
    groupModule.actions.updateCurrentGroupId(to.params.groupId as string)
    studentModule.actions.updateCurrentStudentId(to.params.studentId as string)

    componentKey.value += 1
    totalProgress.value = 0
    progressComplete.value = false

    // Start polling on swap
    checkLoading()
    next()
})

onMounted(async () => {
    checkLoading()
    const routeGroupId = route.params.groupId
    const routeStudentId = route.params.studentId

    await Promise.all([
        groupModule.actions.updateCurrentGroupId(routeGroupId as string),
        studentModule.actions.updateCurrentStudentId(routeStudentId as string),
        examModule.actions.fetchCurrentStudentCurrentExam(),
        licenseModule.actions.fetchCurrentOrgLicenses(),
    ])
})

watch(() => studentFlaggedQuestions.value.length, () => {
    numOfFlaggedQuestions.value = studentStats.value.flaggedQuestions.length
})
</script>

<template>
    <div class="student-stats">
        <div
            v-if="!isPendingStudent"
            class="student-stats__view">
            <StudentStatsHead
                class="student-stats__head"
            />
            <ProgressBar
                v-show="!progressComplete"
                :key="componentKey"
                aria-label="Loading student stats"
                :aria-valuenow="totalProgress"
                :aria-valuemin="0"
                :aria-valuemax="100"
                role="progressbar"
                tabindex="0"
                :percent-progress="totalProgress"
                class="student-stats__progress-bar"
            />
            <ContentVersionMessaging v-if="!isLoading" />
            <SectionHead
                v-show="progressComplete"
                :title="examName || ''"
                :loading-title="currentStudentStats && currentStudentStats.isLoading"
                icon="exam"
                class="student-stats__exam-head"
            >
                <template
                    v-if="currentStudentStats
                        && currentStudentStats.userInfo.scheduledExamDate
                        && getDaysUntil(currentStudentStats.userInfo.scheduledExamDate) > 0
                    "
                >
                    Scheduled for {{ formatDate(currentStudentStats.userInfo.scheduledExamDate) }}
                    • {{ getDaysUntil(currentStudentStats.userInfo.scheduledExamDate) }} Days
                </template>
            </SectionHead>

            <SubjectInsights
                v-show="progressComplete"
                class="student-stats__subject-insights"
            />

            <div
                v-show="progressComplete"
                class="student-stats__2col"
            >
                <QuizScores />
                <div v-if="studentFlaggedQuestions.length" class="student-stats_flagged-questions">
                    <SectionHead
                        :title="`Flagged Questions <span>(${unreadCount})</span>`"
                        icon="flag"
                    >
                        <PocketLink
                            @click="viewAll()"
                        >
                            View All
                        </PocketLink>
                    </SectionHead>
                    <StudentQuestionCard
                        v-for="question in studentFlaggedQuestions.slice(0,3)"
                        :key="question.serial"
                        class="student-stats__flagged-questions"
                        :question="question"
                        :num-of-flagged-questions="studentFlaggedQuestions.length"
                        :page="page"
                        @click="openQuestionReview(question.serial, 'flagged')"
                        @keypress.enter="openQuestionReview(question.serial, 'flagged')"
                    />
                </div>
                <div
                    v-else-if="!studentFlaggedQuestions.length"
                    class="student-stats__no-flagged-questions"
                >
                    <SectionHead
                        :title="`Flagged Questions <span>(${unreadCount})</span>`"
                        icon="flag"
                    />
                    <EmptyState
                        class="student-stats__empty-state"
                        state="flagged"
                        title="0 Flagged Questions"
                        message="Once students flag questions you will be able to see them here."
                    />
                </div>
            </div>

            <div
                v-show="progressComplete"
                class="student-stats__2col"
            >
                <Activities />
                <Streaks />
            </div>
        </div>
        <div
            v-else
        >
            <PendingStudent />
        </div>
    </div>
</template>

<style lang="scss" scoped>
.student-stats {
    &__head {
        margin-bottom: 56px;
    }

    &__exam-head {
        margin-bottom: 40px;
    }

    &__subject-insights {
        margin-bottom: 65px;
    }

    &__2col {
        display: flex;
        width: 100%;
        justify-content: space-between;
        clear: both;
        margin-bottom: 75px;

        & > div {
            flex: 1;
            max-width: 352px;

            &:not(:last-child) {
                margin-right: 40px;
            }
        }
    }

    &__no-flagged-questions {
        text-align: center;
    }

    &__no-flagged-questions-title {
        color: $brand-black;
        font-size: 15px;
        font-weight: 600;
        line-height: 20px;
        text-align: center;
        margin-bottom: 12px;
    }

    &__no-flagged-questions-message {
        color: $ash;
        font-weight: 500;
        font-size: 14px;
        line-height: 20px;
        text-align: center;
        padding-left: 41px;
        padding-right: 42px;
    }

    &__no-flagged-questions-row {
        margin-top: 32px;
        margin-right: auto;
        margin-bottom: 14px;
        margin-left: 30px;
    }

    &___empty-state-blob {
        position: relative;
        bottom: 0;
        left: 0;
    }

    &__no-flagged-questions-icon {
        height: 33px;
        width: 26px;
        position: relative;
        top: -5px;
        left: -35px;
        color: #ce8500;
    }
}
</style>
